/**
 * WordPress dependencies
 */
import { __ } from '@wordpress/i18n';
import {
	addCard,
	addSubmenu,
	alignCenter,
	alignJustify,
	alignLeft,
	alignNone,
	alignRight,
	archive,
	arrowDown,
	arrowLeft,
	arrowRight,
	arrowUp,
	atSymbol,
	aspectRatio,
	audio,
	backup,
	blockDefault,
	blockTable,
	box,
	brush,
	bug,
	button,
	buttons,
	calendar,
	cancelCircleFilled,
	capturePhoto,
	captureVideo,
	category,
	chartBar,
	check,
	chevronDown,
	chevronLeft,
	chevronRight,
	chevronRightSmall,
	chevronUp,
	classic,
	close,
	closeSmall,
	cloudUpload,
	cloud,
	code,
	cog,
	color,
	column,
	columns,
	comment,
	commentAuthorAvatar,
	commentAuthorName,
	commentContent,
	// commentEditLink, the WP Icons NPM package does not yet include this icon.
	commentReplyLink,
	cover,
	create,
	crop,
	currencyDollar,
	currencyEuro,
	currencyPound,
	customPostType,
	desktop,
	dragHandle,
	download,
	edit,
	external,
	file,
	flipHorizontal,
	flipVertical,
	formatBold,
	formatCapitalize,
	formatIndent,
	formatIndentRTL,
	formatItalic,
	formatListBullets,
	formatListBulletsRTL,
	formatListNumbered,
	formatListNumberedRTL,
	formatLtr,
	formatLowercase,
	formatOutdent,
	formatOutdentRTL,
	formatRtl,
	formatStrikethrough,
	formatUnderline,
	formatUppercase,
	fullscreen,
	gallery,
	globe,
	grid,
	group,
	handle,
	heading,
	help,
	helpFilled,
	inbox,
	institution,
	home,
	html,
	image,
	info,
	insertAfter,
	insertBefore,
	justifyLeft,
	justifyCenter,
	justifyRight,
	justifySpaceBetween,
	key,
	keyboardClose,
	keyboardReturn,
	layout,
	lifesaver,
	// There are some issues with these icons.
	// lineDashed,
	// lineDotted,
	// lineSolid,
	link,
	linkOff,
	list,
	listView,
	lock,
	login,
	loop,
	mapMarker,
	media,
	mediaAndText,
	megaphone,
	menu,
	mobile,
	more,
	moreHorizontal,
	// moreHorizontalMobile,
	moreVertical,
	moveTo,
	navigation,
	overlayText,
	pageBreak,
	customLink,
	page,
	pages,
	paragraph,
	payment,
	percent,
	positionCenter,
	positionLeft,
	positionRight,
	pencil,
	people,
	pin,
	plugins,
	plusCircleFilled,
	plusCircle,
	plus,
	// post,
	postAuthor,
	postCategories,
	postContent,
	postComments,
	postCommentsCount,
	postCommentsForm,
	postDate,
	postExcerpt,
	postFeaturedImage,
	postList,
	postTerms,
	previous,
	next,
	preformatted,
	pullLeft,
	pullRight,
	pullquote,
	queryPagination,
	queryPaginationNext,
	queryPaginationNumbers,
	queryPaginationPrevious,
	quote,
	receipt,
	redo,
	removeBug,
	removeSubmenu,
	replace,
	reset,
	resizeCornerNE,
	reusableBlock,
	rotateLeft,
	rotateRight,
	// row,
	rss,
	search,
	separator,
	settings,
	share,
	shield,
	shortcode,
	siteLogo,
	stack,
	starEmpty,
	starFilled,
	starHalf,
	store,
	stretchFullWidth,
	styles,
	shipping,
	stretchWide,
	subscript,
	superscript,
	swatch,
	symbol,
	symbolFilled,
	tableColumnAfter,
	tableColumnBefore,
	tableColumnDelete,
	tableRowAfter,
	tableRowBefore,
	tableRowDelete,
	table,
	tag,
	termDescription,
	footer,
	header,
	sidebar,
	textColor,
	tablet,
	tip,
	title,
	tool,
	trash,
	trendingDown,
	trendingUp,
	typography,
	undo,
	ungroup,
	unlock,
	update,
	upload,
	verse,
	video,
	warning,
	widget,
} from '@wordpress/icons';

/**
 * Internal dependencies
 */
import {
	amazon,
	bandcamp,
	behance,
	chain,
	codepen,
	deviantart,
	dribbble,
	dropbox,
	etsy,
	facebook,
	feed,
	fivehundredpx,
	flickr,
	foursquare,
	goodreads,
	google,
	github,
	instagram,
	lastfm,
	linkedin,
	mail,
	mastodon,
	meetup,
	medium,
	patreon,
	pinterest,
	pocket,
	redditSolid,
	reddit,
	skype,
	snapchat,
	soundcloud,
	spotify,
	telegram,
	tiktok,
	tumblr,
	twitch,
	twitter,
	vimeo,
	vk,
	wordpress,
	yelp,
	youtube,
} from './wordpress/social';
import { fontAwesomeCategories, fontAwesomeIcons } from './font-awesome';

// Temporary icon specifications that are not yet in the NPM package.
import {
	commentEditLink,
	sparkles,
	AlphaAlignStart,
	AlphaAlignCenter,
	AlphaAlignEnd,
	AlphaAlignSpaceAround,
	AlphaAlignSpaceBetween,
	AlphaAlignSpaceEvenly,
	AlphaJustifyStart,
	AlphaJustifyCenter,
	AlphaJustifyEnd,
	AlphaJustifySpaceAround,
	AlphaJustifySpaceBetween,
	AlphaJustifySpaceEvenly,
} from './wordpress/temp';

const wordpressSocialIcons = [
	{
		isDefault: true,
		name: 'wordpress',
		title: 'WordPress',
		icon: wordpress,
		categories: [ 'logos' ],
	},
	{
		name: 'fivehundredpx',
		title: '500px',
		icon: fivehundredpx,
		categories: [ 'logos' ],
	},
	{
		name: 'amazon',
		title: 'Amazon',
		icon: amazon,
		categories: [ 'logos' ],
	},
	{
		name: 'bandcamp',
		title: 'Bandcamp',
		icon: bandcamp,
		categories: [ 'logos' ],
	},
	{
		name: 'behance',
		title: 'Behance',
		icon: behance,
		categories: [ 'logos' ],
	},
	{
		name: 'chain',
		title: 'Link',
		icon: chain,
	},
	{
		name: 'codepen',
		title: 'CodePen',
		icon: codepen,
		categories: [ 'logos' ],
	},
	{
		name: 'deviantart',
		title: 'DeviantArt',
		icon: deviantart,
		categories: [ 'logos' ],
	},
	{
		name: 'dribbble',
		title: 'Dribbble',
		icon: dribbble,
		categories: [ 'logos' ],
	},
	{
		name: 'dropbox',
		title: 'Dropbox',
		icon: dropbox,
		categories: [ 'logos' ],
	},
	{
		name: 'etsy',
		title: 'Etsy',
		icon: etsy,
		categories: [ 'logos' ],
	},
	{
		name: 'facebook',
		title: 'Facebook',
		icon: facebook,
		categories: [ 'logos' ],
	},
	{
		name: 'feed',
		title: 'RSS Feed',
		icon: feed,
		categories: [ 'logos' ],
	},
	{
		name: 'flickr',
		title: 'Flickr',
		icon: flickr,
		categories: [ 'logos' ],
	},
	{
		name: 'foursquare',
		title: 'Foursquare',
		icon: foursquare,
		categories: [ 'logos' ],
	},
	{
		name: 'goodreads',
		title: 'Goodreads',
		icon: goodreads,
		categories: [ 'logos' ],
	},
	{
		name: 'google',
		title: 'Google',
		icon: google,
		categories: [ 'logos' ],
	},
	{
		name: 'github',
		title: 'GitHub',
		icon: github,
		categories: [ 'logos' ],
	},
	{
		name: 'instagram',
		title: 'Instagram',
		icon: instagram,
		categories: [ 'logos' ],
	},
	{
		name: 'lastfm',
		title: 'Last.fm',
		icon: lastfm,
		categories: [ 'logos' ],
	},
	{
		name: 'linkedin',
		title: 'LinkedIn',
		icon: linkedin,
		categories: [ 'logos' ],
	},
	{
		name: 'mail',
		title: 'Mail',
		categories: [ 'email', 'e-mail' ],
		icon: mail,
	},
	{
		name: 'mastodon',
		title: 'Mastodon',
		icon: mastodon,
		categories: [ 'logos' ],
	},
	{
		name: 'meetup',
		title: 'Meetup',
		icon: meetup,
		categories: [ 'logos' ],
	},
	{
		name: 'medium',
		title: 'Medium',
		icon: medium,
		categories: [ 'logos' ],
	},
	{
		name: 'patreon',
		title: 'Patreon',
		icon: patreon,
		categories: [ 'logos' ],
	},
	{
		name: 'pinterest',
		title: 'Pinterest',
		icon: pinterest,
		categories: [ 'logos' ],
	},
	{
		name: 'pocket',
		title: 'Pocket',
		icon: pocket,
		categories: [ 'logos' ],
	},
	{
		name: 'reddit-solid',
		title: 'Reddit Solid',
		icon: redditSolid,
		categories: [ 'logos' ],
	},
	{
		name: 'reddit',
		title: 'Reddit',
		icon: reddit,
		categories: [ 'logos' ],
	},
	{
		name: 'skype',
		title: 'Skype',
		icon: skype,
		categories: [ 'logos' ],
	},
	{
		name: 'snapchat',
		title: 'Snapchat',
		icon: snapchat,
		categories: [ 'logos' ],
	},
	{
		name: 'soundcloud',
		title: 'SoundCloud',
		icon: soundcloud,
		categories: [ 'logos' ],
	},
	{
		name: 'spotify',
		title: 'Spotify',
		icon: spotify,
		categories: [ 'logos' ],
	},
	{
		name: 'telegram',
		title: 'Telegram',
		icon: telegram,
		categories: [ 'logos' ],
	},
	{
		name: 'tiktok',
		title: 'TikTok',
		icon: tiktok,
		categories: [ 'logos' ],
	},
	{
		name: 'tumblr',
		title: 'Tumblr',
		icon: tumblr,
		categories: [ 'logos' ],
	},
	{
		name: 'twitch',
		title: 'Twitch',
		icon: twitch,
		categories: [ 'logos' ],
	},
	{
		isDefault: true,
		name: 'twitter',
		title: 'Twitter',
		icon: twitter,
		categories: [ 'logos' ],
	},
	{
		name: 'vimeo',
		title: 'Vimeo',
		icon: vimeo,
		categories: [ 'logos' ],
	},
	{
		name: 'vk',
		title: 'VK',
		icon: vk,
		categories: [ 'logos' ],
	},
	{
		name: 'yelp',
		title: 'Yelp',
		icon: yelp,
		categories: [ 'logos' ],
	},
	{
		name: 'youtube',
		title: 'YouTube',
		icon: youtube,
		categories: [ 'logos' ],
	},
];

const wordpressIcons = [
	{
		name: 'addCard',
		title: __( 'Add Card', 'iconblock' ),
		icon: addCard,
	},
	{
		name: 'AlphaAlignStart',
		title: __( 'AlphaAlignStart', 'iconblock' ),
		icon: AlphaAlignStart,
		categories: [ 'editor' ],
	},
	{
		name: 'AlphaAlignCenter',
		title: __( 'AlphaAlignCenter', 'iconblock' ),
		icon: AlphaAlignCenter,
		categories: [ 'editor' ],
	},
	{
		name: 'AlphaAlignEnd',
		title: __( 'AlphaAlignEnd', 'iconblock' ),
		icon: AlphaAlignEnd,
		categories: [ 'editor' ],
	},
	{
		name: 'AlphaAlignSpaceAround',
		title: __( 'AlphaAlignSpaceAround', 'iconblock' ),
		icon: AlphaAlignSpaceAround,
		categories: [ 'editor' ],
	},
	{
		name: 'AlphaAlignSpaceBetween',
		title: __( 'AlphaAlignSpaceBetween', 'iconblock' ),
		icon: AlphaAlignSpaceBetween,
		categories: [ 'editor' ],
	},
	{
		name: 'AlphaAlignSpaceEvenly',
		title: __( 'AlphaAlignSpaceEvenly', 'iconblock' ),
		icon: AlphaAlignSpaceEvenly,
		categories: [ 'editor' ],
	},
	{
		name: 'AlphaJustifyStart',
		title: __( 'AlphaJustifyStart', 'iconblock' ),
		icon: AlphaJustifyStart,
		categories: [ 'editor' ],
	},
	{
		name: 'AlphaJustifyCenter',
		title: __( 'AlphaJustifyCenter', 'iconblock' ),
		icon: AlphaJustifyCenter,
		categories: [ 'editor' ],
	},
	{
		name: 'AlphaJustifyEnd',
		title: __( 'AlphaJustifyEnd', 'iconblock' ),
		icon: AlphaJustifyEnd,
		categories: [ 'editor' ],
	},
	{
		name: 'AlphaJustifySpaceAround',
		title: __( 'AlphaJustifySpaceAround', 'iconblock' ),
		icon: AlphaJustifySpaceAround,
		categories: [ 'editor' ],
	},
	{
		name: 'AlphaJustifySpaceBetween',
		title: __( 'AlphaJustifySpaceBetween', 'iconblock' ),
		icon: AlphaJustifySpaceBetween,
		categories: [ 'editor' ],
	},
	{
		name: 'AlphaJustifySpaceEvenly',
		title: __( 'AlphaJustifySpaceEvenly', 'iconblock' ),
		icon: AlphaJustifySpaceEvenly,
		categories: [ 'editor' ],
	},
	{
		name: 'addSubmenu',
		title: __( 'Add Submenu', 'iconblock' ),
		icon: addSubmenu,
		categories: [ 'arrows' ],
	},
	{
		name: 'alignCenter',
		title: __( 'Align Center', 'iconblock' ),
		icon: alignCenter,
		categories: [ 'editor' ],
	},
	{
		name: 'alignJustify',
		title: __( 'Align Justify', 'iconblock' ),
		icon: alignJustify,
		categories: [ 'editor' ],
	},
	{
		name: 'alignLeft',
		title: __( 'Align Left', 'iconblock' ),
		icon: alignLeft,
		categories: [ 'editor' ],
	},
	{
		name: 'alignNone',
		title: __( 'Align None', 'iconblock' ),
		icon: alignNone,
		categories: [ 'editor' ],
	},
	{
		name: 'alignRight',
		title: __( 'Align Right', 'iconblock' ),
		icon: alignRight,
		categories: [ 'editor' ],
	},
	{
		name: 'archive',
		title: __( 'Archive', 'iconblock' ),
		icon: archive,
		categories: [ 'blocks' ],
	},
	{
		name: 'arrowDown',
		title: __( 'Arrow Down', 'iconblock' ),
		icon: arrowDown,
		categories: [ 'arrows' ],
	},
	{
		name: 'arrowLeft',
		title: __( 'Arrow Left', 'iconblock' ),
		icon: arrowLeft,
		categories: [ 'arrows' ],
	},
	{
		name: 'arrowRight',
		title: __( 'Arrow Right', 'iconblock' ),
		icon: arrowRight,
		categories: [ 'arrows' ],
	},
	{
		name: 'arrowUp',
		title: __( 'Arrow Up', 'iconblock' ),
		icon: arrowUp,
		categories: [ 'arrows' ],
	},
	{
		name: 'atSymbol',
		title: __( 'At Symbol', 'iconblock' ),
		icon: atSymbol,
	},
	{
		name: 'aspectRatio',
		title: __( 'Aspect Ratio', 'iconblock' ),
		icon: aspectRatio,
		categories: [ 'media' ],
	},
	{
		name: 'audio',
		title: __( 'Audio', 'iconblock' ),
		icon: audio,
		categories: [ 'media' ],
	},
	{
		name: 'backup',
		title: __( 'Backup', 'iconblock' ),
		icon: backup,
	},
	{
		name: 'blockDefault',
		title: __( 'Block Default', 'iconblock' ),
		icon: blockDefault,
		categories: [ 'blocks' ],
	},
	{
		name: 'blockTable',
		title: __( 'Block Table', 'iconblock' ),
		icon: blockTable,
	},
	{
		name: 'box',
		title: __( 'Box', 'iconblock' ),
		icon: box,
	},
	{
		name: 'brush',
		title: __( 'Brush', 'iconblock' ),
		icon: brush,
	},
	{
		name: 'bug',
		title: __( 'Bug', 'iconblock' ),
		icon: bug,
	},
	{
		name: 'button',
		title: __( 'Button', 'iconblock' ),
		icon: button,
		categories: [ 'blocks' ],
	},
	{
		name: 'buttons',
		title: __( 'Buttons', 'iconblock' ),
		icon: buttons,
		categories: [ 'blocks' ],
	},
	{
		name: 'calendar',
		title: __( 'Calendar', 'iconblock' ),
		icon: calendar,
		categories: [ 'blocks' ],
	},
	{
		name: 'cancelCircleFilled',
		title: __( 'Cancel - Circle Filled', 'iconblock' ),
		icon: cancelCircleFilled,
	},
	{
		name: 'capturePhoto',
		title: __( 'Capture Photo', 'iconblock' ),
		icon: capturePhoto,
		categories: [ 'media' ],
	},
	{
		name: 'captureVideo',
		title: __( 'Capture Video', 'iconblock' ),
		icon: captureVideo,
		categories: [ 'media' ],
	},
	{
		name: 'category',
		title: __( 'Category', 'iconblock' ),
		icon: category,
		categories: [ 'blocks' ],
	},
	{
		name: 'chartBar',
		title: __( 'Chart Bar', 'iconblock' ),
		icon: chartBar,
	},
	{
		name: 'check',
		title: __( 'Check', 'iconblock' ),
		icon: check,
	},
	{
		name: 'chevronDown',
		title: __( 'Chevron Down', 'iconblock' ),
		icon: chevronDown,
		categories: [ 'arrows' ],
	},
	{
		name: 'chevronLeft',
		title: __( 'Chevron Left', 'iconblock' ),
		icon: chevronLeft,
		categories: [ 'arrows' ],
	},
	{
		name: 'chevronRight',
		title: __( 'Chevron Right', 'iconblock' ),
		icon: chevronRight,
		categories: [ 'arrows' ],
	},
	{
		name: 'chevronRightSmall',
		title: __( 'Chevron Right - Small', 'iconblock' ),
		icon: chevronRightSmall,
		categories: [ 'arrows' ],
	},
	{
		name: 'chevronUp',
		title: __( 'Chevron Up', 'iconblock' ),
		icon: chevronUp,
		categories: [ 'arrows' ],
	},
	{
		name: 'classic',
		title: __( 'Classic', 'iconblock' ),
		icon: classic,
		categories: [ 'blocks' ],
	},
	{
		name: 'close',
		title: __( 'Close', 'iconblock' ),
		icon: close,
	},
	{
		name: 'closeSmall',
		title: __( 'Close - Small', 'iconblock' ),
		icon: closeSmall,
	},
	{
		name: 'cloudUpload',
		title: __( 'Cloud Upload', 'iconblock' ),
		icon: cloudUpload,
		categories: [ 'arrows' ],
	},
	{
		name: 'cloud',
		title: __( 'Cloud', 'iconblock' ),
		icon: cloud,
	},
	{
		name: 'code',
		title: __( 'Code', 'iconblock' ),
		icon: code,
		categories: [ 'blocks' ],
	},
	{
		name: 'cog',
		title: __( 'Cog', 'iconblock' ),
		icon: cog,
	},
	{
		name: 'color',
		title: __( 'Color', 'iconblock' ),
		icon: color,
	},
	{
		name: 'column',
		title: __( 'Column', 'iconblock' ),
		icon: column,
		categories: [ 'blocks' ],
	},
	{
		name: 'columns',
		title: __( 'Columns', 'iconblock' ),
		icon: columns,
		categories: [ 'blocks' ],
	},
	{
		name: 'comment',
		title: __( 'Comment', 'iconblock' ),
		icon: comment,
		categories: [ 'blocks' ],
	},
	{
		name: 'commentAuthorAvatar',
		title: __( 'Comment Author Avatar', 'iconblock' ),
		icon: commentAuthorAvatar,
		categories: [ 'blocks' ],
	},
	{
		name: 'commentAuthorName',
		title: __( 'Comment Author Name', 'iconblock' ),
		icon: commentAuthorName,
		categories: [ 'blocks' ],
	},
	{
		name: 'commentContent',
		title: __( 'Comment Content', 'iconblock' ),
		icon: commentContent,
		categories: [ 'blocks' ],
	},
	{
		name: 'commentEditLink',
		title: __( 'Comment Edit Link', 'iconblock' ),
		icon: commentEditLink,
		categories: [ 'blocks' ],
	},
	{
		name: 'commentReplyLink',
		title: __( 'Comment Reply Link', 'iconblock' ),
		icon: commentReplyLink,
		categories: [ 'blocks' ],
	},
	{
		name: 'cover',
		title: __( 'Cover', 'iconblock' ),
		icon: cover,
		categories: [ 'blocks' ],
	},
	{
		name: 'create',
		title: __( 'Create', 'iconblock' ),
		icon: create,
	},
	{
		name: 'crop',
		title: __( 'Crop', 'iconblock' ),
		icon: crop,
	},
	{
		name: 'currencyDollar',
		title: __( 'Currency Dollar', 'iconblock' ),
		icon: currencyDollar,
	},
	{
		name: 'currencyEuro',
		title: __( 'Currency Euro', 'iconblock' ),
		icon: currencyEuro,
	},
	{
		name: 'currencyPound',
		title: __( 'Currency Pound', 'iconblock' ),
		icon: currencyPound,
	},
	{
		name: 'customPostType',
		title: __( 'Custom Post Type', 'iconblock' ),
		icon: customPostType,
	},
	{
		name: 'desktop',
		title: __( 'Desktop', 'iconblock' ),
		icon: desktop,
		categories: [ 'devices' ],
	},
	{
		name: 'dragHandle',
		title: __( 'Drag Handle', 'iconblock' ),
		icon: dragHandle,
	},
	{
		name: 'download',
		title: __( 'Download', 'iconblock' ),
		icon: download,
		categories: [ 'arrows' ],
	},
	{
		name: 'edit',
		title: __( 'Edit', 'iconblock' ),
		icon: edit,
	},
	{
		name: 'external',
		title: __( 'External', 'iconblock' ),
		icon: external,
	},
	{
		name: 'file',
		title: __( 'File', 'iconblock' ),
		icon: file,
	},
	{
		name: 'flipHorizontal',
		title: __( 'Flip Horizontal', 'iconblock' ),
		icon: flipHorizontal,
	},
	{
		name: 'flipVertical',
		title: __( 'Flip Vertical', 'iconblock' ),
		icon: flipVertical,
	},
	{
		name: 'formatBold',
		title: __( 'Format Bold', 'iconblock' ),
		icon: formatBold,
		categories: [ 'editor' ],
	},
	{
		name: 'formatCapitalize',
		title: __( 'Format Capitalize', 'iconblock' ),
		icon: formatCapitalize,
		categories: [ 'editor' ],
	},
	{
		name: 'formatIndent',
		title: __( 'Format Indent', 'iconblock' ),
		icon: formatIndent,
		categories: [ 'editor' ],
	},
	{
		name: 'formatIndentRTL',
		title: __( 'Format Indent RTL', 'iconblock' ),
		icon: formatIndentRTL,
	},
	{
		name: 'formatItalic',
		title: __( 'Format Italic', 'iconblock' ),
		icon: formatItalic,
		categories: [ 'editor' ],
	},
	{
		name: 'formatListBullets',
		title: __( 'Format List Bullets', 'iconblock' ),
		icon: formatListBullets,
		categories: [ 'editor' ],
	},
	{
		name: 'formatListBulletsRTL',
		title: __( 'Format List Bullets RTL', 'iconblock' ),
		icon: formatListBulletsRTL,
		categories: [ 'editor' ],
	},
	{
		name: 'formatListNumbered',
		title: __( 'Format List Numbered', 'iconblock' ),
		icon: formatListNumbered,
		categories: [ 'editor' ],
	},
	{
		name: 'formatListNumberedRTL',
		title: __( 'Format List Numbered RTL', 'iconblock' ),
		icon: formatListNumberedRTL,
		categories: [ 'editor' ],
	},
	{
		name: 'formatLtr',
		title: __( 'Format LTR', 'iconblock' ),
		icon: formatLtr,
		categories: [ 'editor' ],
	},
	{
		name: 'formatLowercase',
		title: __( 'Format Lowercase', 'iconblock' ),
		icon: formatLowercase,
		categories: [ 'editor' ],
	},
	{
		name: 'formatOutdent',
		title: __( 'Format Outdent', 'iconblock' ),
		icon: formatOutdent,
		categories: [ 'editor' ],
	},
	{
		name: 'formatOutdentRTL',
		title: __( 'Format Outdent RTL', 'iconblock' ),
		icon: formatOutdentRTL,
		categories: [ 'editor' ],
	},
	{
		name: 'formatRtl',
		title: __( 'Format RTL', 'iconblock' ),
		icon: formatRtl,
		categories: [ 'editor' ],
	},
	{
		name: 'formatStrikethrough',
		title: __( 'Format Strikethrough', 'iconblock' ),
		icon: formatStrikethrough,
		categories: [ 'editor' ],
	},
	{
		name: 'formatUnderline',
		title: __( 'Format Underline', 'iconblock' ),
		icon: formatUnderline,
		categories: [ 'editor' ],
	},
	{
		name: 'formatUppercase',
		title: __( 'Format Uppercase', 'iconblock' ),
		icon: formatUppercase,
		categories: [ 'editor' ],
	},
	{
		name: 'fullscreen',
		title: __( 'Fullscreen', 'iconblock' ),
		icon: fullscreen,
		categories: [ 'editor' ],
	},
	{
		name: 'gallery',
		title: __( 'Gallery', 'iconblock' ),
		icon: gallery,
		categories: [ 'blocks', 'media' ],
	},
	{
		name: 'globe',
		title: __( 'Globe', 'iconblock' ),
		icon: globe,
	},
	{
		name: 'grid',
		title: __( 'Grid', 'iconblock' ),
		icon: grid,
	},
	{
		name: 'group',
		title: __( 'Group', 'iconblock' ),
		icon: group,
		categories: [ 'blocks' ],
	},
	{
		name: 'handle',
		title: __( 'Handle', 'iconblock' ),
		icon: handle,
	},
	{
		name: 'heading',
		title: __( 'Heading', 'iconblock' ),
		icon: heading,
		categories: [ 'blocks' ],
	},
	{
		name: 'help',
		title: __( 'Help', 'iconblock' ),
		icon: help,
	},
	{
		name: 'helpFilled',
		title: __( 'Help - Filled', 'iconblock' ),
		icon: helpFilled,
	},
	{
		name: 'inbox',
		title: __( 'Inbox', 'iconblock' ),
		icon: inbox,
	},
	{
		name: 'institution',
		title: __( 'Institution', 'iconblock' ),
		icon: institution,
	},
	{
		name: 'home',
		title: __( 'Home', 'iconblock' ),
		icon: home,
		categories: [ 'blocks' ],
	},
	{
		name: 'html',
		title: __( 'HTML', 'iconblock' ),
		icon: html,
		categories: [ 'blocks' ],
	},
	{
		isDefault: true,
		name: 'image',
		title: __( 'Image', 'iconblock' ),
		icon: image,
		categories: [ 'blocks', 'media' ],
	},
	{
		name: 'info',
		title: __( 'Info', 'iconblock' ),
		icon: info,
	},
	{
		name: 'insertAfter',
		title: __( 'Insert After', 'iconblock' ),
		icon: insertAfter,
		categories: [ 'editor' ],
	},
	{
		name: 'insertBefore',
		title: __( 'Insert Before', 'iconblock' ),
		icon: insertBefore,
		categories: [ 'editor' ],
	},
	{
		name: 'justifyLeft',
		title: __( 'Justify Left', 'iconblock' ),
		icon: justifyLeft,
		categories: [ 'editor' ],
	},
	{
		name: 'justifyCenter',
		title: __( 'Justify Center', 'iconblock' ),
		icon: justifyCenter,
		categories: [ 'editor' ],
	},
	{
		name: 'justifyRight',
		title: __( 'Justify Right', 'iconblock' ),
		icon: justifyRight,
		categories: [ 'editor' ],
	},
	{
		name: 'justifySpaceBetween',
		title: __( 'Justify Space Between', 'iconblock' ),
		icon: justifySpaceBetween,
		categories: [ 'editor' ],
	},
	{
		name: 'key',
		title: __( 'Key', 'iconblock' ),
		icon: key,
	},
	{
		name: 'keyboardClose',
		title: __( 'Keyboard Close', 'iconblock' ),
		icon: keyboardClose,
		categories: [ 'editor' ],
	},
	{
		name: 'keyboardReturn',
		title: __( 'Keyboard Return', 'iconblock' ),
		icon: keyboardReturn,
		categories: [ 'editor' ],
	},
	{
		name: 'layout',
		title: __( 'Layout', 'iconblock' ),
		icon: layout,
	},
	{
		name: 'lifesaver',
		title: __( 'Lifesaver', 'iconblock' ),
		icon: lifesaver,
	},
	// There are some issues with these icons.
	// {
	// 	name: 'lineDashed',
	// 	title:  __( 'Line Dashed', 'iconblock' ),
	// 	icon: lineDashed,
	// },
	// {
	// 	name: 'lineDotted',
	// 	title:  __( 'Line Dotted', 'iconblock' ),
	// 	icon: lineDotted,
	// },
	// {
	// 	name: 'lineSolid',
	// 	title:  __( 'Line Solid', 'iconblock' ),
	// 	icon: lineSolid,
	// },
	{
		name: 'link',
		title: __( 'Link', 'iconblock' ),
		icon: link,
		categories: [ 'editor' ],
	},
	{
		name: 'linkOff',
		title: __( 'Link Off', 'iconblock' ),
		icon: linkOff,
		categories: [ 'editor' ],
	},
	{
		name: 'list',
		title: __( 'List', 'iconblock' ),
		icon: list,
		categories: [ 'blocks' ],
	},
	{
		name: 'listView',
		title: __( 'List View', 'iconblock' ),
		icon: listView,
		categories: [ 'editor' ],
	},
	{
		name: 'lock',
		title: __( 'Lock', 'iconblock' ),
		icon: lock,
		categories: [ 'editor' ],
	},
	{
		name: 'login',
		title: __( 'Login', 'iconblock' ),
		icon: login,
	},
	{
		name: 'loop',
		title: __( 'Loop', 'iconblock' ),
		icon: loop,
		categories: [ 'blocks' ],
	},
	{
		name: 'mapMarker',
		title: __( 'Map Marker', 'iconblock' ),
		icon: mapMarker,
	},
	{
		name: 'media',
		title: __( 'Media', 'iconblock' ),
		icon: media,
		categories: [ 'blocks', 'media' ],
	},
	{
		name: 'mediaAndText',
		title: __( 'Media & Text', 'iconblock' ),
		icon: mediaAndText,
		categories: [ 'blocks', 'media' ],
	},
	{
		name: 'megaphone',
		title: __( 'Megaphone', 'iconblock' ),
		icon: megaphone,
	},
	{
		name: 'menu',
		title: __( 'Menu', 'iconblock' ),
		icon: menu,
	},
	{
		name: 'mobile',
		title: __( 'Mobile', 'iconblock' ),
		icon: mobile,
		categories: [ 'devices' ],
	},
	{
		name: 'more',
		title: __( 'More', 'iconblock' ),
		icon: more,
	},
	{
		name: 'moreHorizontal',
		title: __( 'More Horizontal', 'iconblock' ),
		icon: moreHorizontal,
	},
	// {
	// 	name: 'moreHorizontalMobile',
	// 	title: __( 'More Horizontal - Mobile', 'iconblock' ),
	// 	icon: moreHorizontalMobile,
	// },
	{
		name: 'moreVertical',
		title: __( 'More Vertical', 'iconblock' ),
		icon: moreVertical,
	},
	{
		name: 'moveTo',
		title: __( 'Move To', 'iconblock' ),
		icon: moveTo,
		categories: [ 'arrows' ],
	},
	{
		name: 'navigation',
		title: __( 'Navigation', 'iconblock' ),
		icon: navigation,
	},
	{
		name: 'overlayText',
		title: __( 'Overlay Text', 'iconblock' ),
		icon: overlayText,
	},
	{
		name: 'pageBreak',
		title: __( 'Page Break', 'iconblock' ),
		icon: pageBreak,
	},
	{
		name: 'customLink',
		title: __( 'Custom Link', 'iconblock' ),
		icon: customLink,
	},
	{
		name: 'page',
		title: __( 'Page', 'iconblock' ),
		icon: page,
	},
	{
		name: 'pages',
		title: __( 'Pages', 'iconblock' ),
		icon: pages,
	},
	{
		name: 'paragraph',
		title: __( 'Paragraph', 'iconblock' ),
		icon: paragraph,
	},
	{
		name: 'payment',
		title: __( 'Payment', 'iconblock' ),
		icon: payment,
	},
	{
		name: 'percent',
		title: __( 'Percent', 'iconblock' ),
		icon: percent,
	},
	{
		name: 'positionCenter',
		title: __( 'Position Center', 'iconblock' ),
		icon: positionCenter,
	},
	{
		name: 'positionLeft',
		title: __( 'Position Left', 'iconblock' ),
		icon: positionLeft,
	},
	{
		name: 'positionRight',
		title: __( 'Position Right', 'iconblock' ),
		icon: positionRight,
	},
	// {
	// 	name: "post",
	// 	title: __("Post", "iconblock"),
	// 	icon: post,
	// },
	{
		name: 'pencil',
		title: __( 'Pencil', 'iconblock' ),
		icon: pencil,
	},
	{
		name: 'people',
		title: __( 'People', 'iconblock' ),
		icon: people,
	},
	{
		name: 'pin',
		title: __( 'Pin', 'iconblock' ),
		icon: pin,
	},
	{
		name: 'plugins',
		title: __( 'Plugins', 'iconblock' ),
		icon: plugins,
	},
	{
		name: 'plusCircleFilled',
		title: __( 'Plus Circle - Filled', 'iconblock' ),
		icon: plusCircleFilled,
	},
	{
		name: 'plusCircle',
		title: __( 'Plus Circle', 'iconblock' ),
		icon: plusCircle,
	},
	{
		name: 'plus',
		title: __( 'Plus', 'iconblock' ),
		icon: plus,
	},
	{
		name: 'postAuthor',
		title: __( 'Post Author', 'iconblock' ),
		icon: postAuthor,
		categories: [ 'blocks' ],
	},
	{
		name: 'postCategories',
		title: __( 'Post Categories', 'iconblock' ),
		icon: postCategories,
		categories: [ 'blocks' ],
	},
	{
		name: 'postContent',
		title: __( 'Post Content', 'iconblock' ),
		icon: postContent,
		categories: [ 'blocks' ],
	},
	{
		name: 'postComments',
		title: __( 'Post Comments', 'iconblock' ),
		icon: postComments,
		categories: [ 'blocks' ],
	},
	{
		name: 'postCommentsCount',
		title: __( 'Post Comment Count', 'iconblock' ),
		icon: postCommentsCount,
	},
	{
		name: 'postCommentsForm',
		title: __( 'Post Comments Form', 'iconblock' ),
		icon: postCommentsForm,
		categories: [ 'blocks' ],
	},
	{
		name: 'postDate',
		title: __( 'Post Date', 'iconblock' ),
		icon: postDate,
		categories: [ 'blocks' ],
	},
	{
		name: 'postExcerpt',
		title: __( 'Post Excerpt', 'iconblock' ),
		icon: postExcerpt,
		categories: [ 'blocks' ],
	},
	{
		name: 'postFeaturedImage',
		title: __( 'Post Featured Image', 'iconblock' ),
		icon: postFeaturedImage,
		categories: [ 'blocks' ],
	},
	{
		name: 'postList',
		title: __( 'Post List', 'iconblock' ),
		icon: postList,
		categories: [ 'blocks' ],
	},
	{
		name: 'postTerms',
		title: __( 'Post Terms', 'iconblock' ),
		icon: postTerms,
		categories: [ 'blocks' ],
	},
	{
		name: 'previous',
		title: __( 'Previous', 'iconblock' ),
		icon: previous,
		categories: [ 'arrows' ],
	},
	{
		name: 'next',
		title: __( 'Next', 'iconblock' ),
		icon: next,
		categories: [ 'arrows' ],
	},
	{
		name: 'preformatted',
		title: __( 'Preformatted', 'iconblock' ),
		icon: preformatted,
	},
	{
		name: 'pullLeft',
		title: __( 'Pull Left', 'iconblock' ),
		icon: pullLeft,
	},
	{
		name: 'pullRight',
		title: __( 'Pull Right', 'iconblock' ),
		icon: pullRight,
	},
	{
		name: 'pullquote',
		title: __( 'Pull Quote', 'iconblock' ),
		icon: pullquote,
	},
	{
		name: 'queryPagination',
		title: __( 'Query Pagination', 'iconblock' ),
		icon: queryPagination,
	},
	{
		name: 'queryPaginationNext',
		title: __( 'Query Pagination Next', 'iconblock' ),
		icon: queryPaginationNext,
		categories: [ 'arrows' ],
	},
	{
		name: 'queryPaginationNumbers',
		title: __( 'Query Pagination Numbers', 'iconblock' ),
		icon: queryPaginationNumbers,
	},
	{
		name: 'queryPaginationPrevious',
		title: __( 'Query Pagination Previous', 'iconblock' ),
		icon: queryPaginationPrevious,
		categories: [ 'arrows' ],
	},
	{
		name: 'quote',
		title: __( 'Quote', 'iconblock' ),
		icon: quote,
		categories: [ 'blocks' ],
	},
	{
		name: 'receipt',
		title: __( 'Receipt', 'iconblock' ),
		icon: receipt,
	},
	{
		name: 'redo',
		title: __( 'Redo', 'iconblock' ),
		icon: redo,
		categories: [ 'arrows', 'editor' ],
	},
	{
		name: 'removeBug',
		title: __( 'Remove Bug', 'iconblock' ),
		icon: removeBug,
	},
	// {
	// 	name: "removeSubmenu",
	// 	title: __("Remove Submenu", "iconblock"),
	// 	icon: removeSubmenu,
	// },
	{
		name: 'replace',
		title: __( 'Replace', 'iconblock' ),
		icon: replace,
		categories: [ 'arrows' ],
	},
	{
		name: 'reset',
		title: __( 'Reset', 'iconblock' ),
		icon: reset,
		categories: [ 'minus' ],
	},
	{
		name: 'resizeCornerNE',
		title: __( 'Resize Corner', 'iconblock' ),
		icon: resizeCornerNE,
		categories: [ 'arrows' ],
	},
	{
		name: 'reusableBlock',
		title: __( 'Reusable Block', 'iconblock' ),
		icon: reusableBlock,
		categories: [ 'arrows' ],
	},
	{
		name: 'rotateLeft',
		title: __( 'Rotate Left', 'iconblock' ),
		icon: rotateLeft,
		categories: [ 'arrows' ],
	},
	{
		name: 'rotateRight',
		title: __( 'Rotate Right', 'iconblock' ),
		icon: rotateRight,
		categories: [ 'arrows' ],
	},
	// {
	// 	name: "row",
	// 	title: __("Row", "iconblock"),
	// 	icon: row,
	// },
	{
		name: 'rss',
		title: __( 'RSS', 'iconblock' ),
		icon: rss,
	},
	{
		name: 'search',
		title: __( 'Search', 'iconblock' ),
		icon: search,
		categories: [ 'magnifying glass' ],
	},
	{
		name: 'separator',
		title: __( 'Separator', 'iconblock' ),
		icon: separator,
		categories: [ 'blocks' ],
	},
	{
		name: 'settings',
		title: __( 'Settings', 'iconblock' ),
		icon: settings,
	},
	{
		name: 'share',
		title: __( 'Share', 'iconblock' ),
		icon: share,
	},
	{
		name: 'shield',
		title: __( 'Shield', 'iconblock' ),
		icon: shield,
	},
	{
		name: 'shortcode',
		title: __( 'Shortcode', 'iconblock' ),
		icon: shortcode,
		categories: [ 'blocks' ],
	},
	{
		name: 'siteLogo',
		title: __( 'Site Logo', 'iconblock' ),
		icon: siteLogo,
		categories: [ 'blocks', 'media' ],
	},
	{
		isDefault: true,
		name: 'sparkles',
		title: __( 'Sparkles', 'iconblock' ),
		icon: sparkles,
	},
	{
		name: 'stack',
		title: __( 'Stack', 'iconblock' ),
		icon: stack,
	},
	{
		name: 'starEmpty',
		title: __( 'Star Empty', 'iconblock' ),
		icon: starEmpty,
	},
	{
		name: 'starFilled',
		title: __( 'Star Filled', 'iconblock' ),
		icon: starFilled,
	},
	{
		name: 'starHalf',
		title: __( 'Star Half', 'iconblock' ),
		icon: starHalf,
	},
	{
		name: 'store',
		title: __( 'Store', 'iconblock' ),
		icon: store,
	},
	{
		name: 'stretchFullWidth',
		title: __( 'Stretch Full Width', 'iconblock' ),
		icon: stretchFullWidth,
		categories: [ 'editor' ],
	},
	{
		name: 'styles',
		title: __( 'Styles', 'iconblock' ),
		icon: styles,
	},
	{
		isDefault: true,
		name: 'shipping',
		title: __( 'Shipping', 'iconblock' ),
		icon: shipping,
		categories: [ 'truck' ],
	},
	{
		name: 'stretchWide',
		title: __( 'Stretch Wide', 'iconblock' ),
		icon: stretchWide,
		categories: [ 'editor' ],
	},
	{
		name: 'subscript',
		title: __( 'Subscript', 'iconblock' ),
		icon: subscript,
		categories: [ 'editor' ],
	},
	{
		name: 'superscript',
		title: __( 'Superscript', 'iconblock' ),
		icon: superscript,
		categories: [ 'editor' ],
	},
	{
		name: 'swatch',
		title: __( 'Swatch', 'iconblock' ),
		icon: swatch,
	},
	{
		name: 'symbol',
		title: __( 'Symbol', 'iconblock' ),
		icon: symbol,
	},
	{
		name: 'symbolFilled',
		title: __( 'Symbol - Filled', 'iconblock' ),
		icon: symbolFilled,
	},
	{
		name: 'tableColumnAfter',
		title: __( 'Table Column After', 'iconblock' ),
		icon: tableColumnAfter,
		categories: [ 'editor' ],
	},
	{
		name: 'tableColumnBefore',
		title: __( 'Table Column Before', 'iconblock' ),
		icon: tableColumnBefore,
		categories: [ 'editor' ],
	},
	{
		name: 'tableColumnDelete',
		title: __( 'Table Column Delete', 'iconblock' ),
		icon: tableColumnDelete,
		categories: [ 'editor' ],
	},
	{
		name: 'tableRowAfter',
		title: __( 'Table Row After', 'iconblock' ),
		icon: tableRowAfter,
		categories: [ 'editor' ],
	},
	{
		name: 'tableRowBefore',
		title: __( 'Table Row Before', 'iconblock' ),
		icon: tableRowBefore,
		categories: [ 'editor' ],
	},
	{
		name: 'tableRowDelete',
		title: __( 'Table Row Delete', 'iconblock' ),
		icon: tableRowDelete,
		categories: [ 'editor' ],
	},
	{
		name: 'table',
		title: __( 'Table', 'iconblock' ),
		icon: table,
	},
	{
		name: 'tag',
		title: __( 'Tag', 'iconblock' ),
		icon: tag,
	},
	{
		name: 'termDescription',
		title: __( 'Term Description', 'iconblock' ),
		icon: termDescription,
		categories: [ 'blocks' ],
	},
	{
		name: 'footer',
		title: __( 'Footer', 'iconblock' ),
		icon: footer,
	},
	{
		name: 'header',
		title: __( 'Header', 'iconblock' ),
		icon: header,
	},
	{
		name: 'sidebar',
		title: __( 'Sidebar', 'iconblock' ),
		icon: sidebar,
	},
	{
		name: 'textColor',
		title: __( 'Text Color', 'iconblock' ),
		icon: textColor,
	},
	{
		name: 'tablet',
		title: __( 'Tablet', 'iconblock' ),
		icon: tablet,
		categories: [ 'devices' ],
	},
	{
		name: 'title',
		title: __( 'Title', 'iconblock' ),
		icon: title,
	},
	{
		name: 'tip',
		title: __( 'Tip', 'iconblock' ),
		icon: tip,
	},
	{
		name: 'tool',
		title: __( 'Tool', 'iconblock' ),
		icon: tool,
	},
	{
		name: 'trash',
		title: __( 'Trash', 'iconblock' ),
		icon: trash,
	},
	{
		name: 'trendingDown',
		title: __( 'Trending Down', 'iconblock' ),
		icon: trendingDown,
		categories: [ 'arrows' ],
	},
	{
		name: 'trendingUp',
		title: __( 'Trending Up', 'iconblock' ),
		icon: trendingUp,
		categories: [ 'arrows' ],
	},
	{
		name: 'typography',
		title: __( 'Typography', 'iconblock' ),
		icon: typography,
	},
	{
		name: 'undo',
		title: __( 'Undo', 'iconblock' ),
		icon: undo,
		categories: [ 'arrows', 'editor' ],
	},
	{
		name: 'ungroup',
		title: __( 'Ungroup', 'iconblock' ),
		icon: ungroup,
		categories: [ 'editor' ],
	},
	{
		name: 'unlock',
		title: __( 'Unlock', 'iconblock' ),
		icon: unlock,
		categories: [ 'editor' ],
	},
	{
		name: 'update',
		title: __( 'Update', 'iconblock' ),
		icon: update,
	},
	{
		name: 'upload',
		title: __( 'Upload', 'iconblock' ),
		icon: upload,
		categories: [ 'arrows' ],
	},
	{
		isDefault: true,
		name: 'verse',
		title: __( 'Verse', 'iconblock' ),
		icon: verse,
		categories: [ 'blocks' ],
	},
	{
		name: 'video',
		title: __( 'Video', 'iconblock' ),
		icon: video,
		categories: [ 'media' ],
	},
	{
		name: 'warning',
		title: __( 'Warning', 'iconblock' ),
		icon: warning,
	},
	{
		name: 'widget',
		title: __( 'Widget', 'iconblock' ),
		icon: widget,
	},
];

export const ultimateIcons = [
	{
		isDefault: false,
		type: 'wordpress',
		title: __( 'WordPress', 'iconblock' ),
		icons: [ ...wordpressSocialIcons, ...wordpressIcons ],
		categories: [
			{
				name: 'arrows',
				title: __( 'Arrows', 'iconblock' ),
			},
			{
				name: 'blocks',
				title: __( 'Blocks', 'iconblock' ),
			},
			{
				name: 'devices',
				title: __( 'Devices', 'iconblock' ),
			},
			{
				name: 'editor',
				title: __( 'Editor', 'iconblock' ),
			},
			{
				name: 'logos',
				title: __( 'Logos', 'iconblock' ),
			},
			{
				name: 'media',
				title: __( 'Media', 'iconblock' ),
			},
		],
	},
	{
		isDefault: false,
		type: 'font-awesome',
		title: __( 'Font Awesome', 'iconblock' ),
		icons: fontAwesomeIcons,
		categories: fontAwesomeCategories,
	},
];
